import useToken from "hooks/useToken";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import { useToast } from 'context/providers/ToastProvider';
import ComentarioItem from './ComentarioItem';

const ComentariosSoporte = ({
    comentarios,
    setComentarios,
    emailCliente
}) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const [editandoId, setEditandoId] = useState(null);
    const [textoEditado, setTextoEditado] = useState("");
    const [resolviendoId, setResolviendoId] = useState(null);
    const [textoSolucion, setTextoSolucion] = useState("");
    const [nivelesSoporte, setNivelesSoporte] = useState([]);
    const [nivelSoporteSeleccionado, setNivelSoporteSeleccionado] = useState("");
    const [minutosInvertidos, setMinutosInvertidos] = useState("");
    const [cargandoNiveles, setCargandoNiveles] = useState(false);
    const [editandoSolucionId, setEditandoSolucionId] = useState(null);
    const [textoSolucionEditado, setTextoSolucionEditado] = useState("");
    const [nivelSoporteEditado, setNivelSoporteEditado] = useState("");
    const [minutosInvertidosEditado, setMinutosInvertidosEditado] = useState("");
    const [imagenesPerfil, setImagenesPerfil] = useState({});
    const [cargandoImagenes, setCargandoImagenes] = useState({});

    const solucionTextareaRef = useRef(null);
    const minutosRef = useRef(null);
    const nivelRef = useRef(null);

    useEffect(() => {
        obtenerNivelesSoporte();
    }, []);

    useEffect(() => {
        if (resolviendoId && solucionTextareaRef.current) {
            solucionTextareaRef.current.focus();
        }
    }, [resolviendoId]);

    useEffect(() => {
        if (comentarios && comentarios.length > 0) {
            const uniqueIds = [...new Set(comentarios.map(c => c.idUsuario))];
            uniqueIds.forEach(id => {
                if (id && !imagenesPerfil[id] && !cargandoImagenes[id]) {
                    cargarImagenPerfil(id);
                }
            });
        }
    }, [comentarios]);

    const iniciarEdicion = (comentario) => {
        setEditandoId(comentario.idClientesAkasiaComentariosProblema);
        const textoSinHTML = comentario.vProblema.replace(/<[^>]*>/g, "");
        setTextoEditado(textoSinHTML);
    };

    const cancelarEdicion = () => {
        setEditandoId(null);
        setTextoEditado("");
    };

    const guardarEdicion = async (comentario) => {
        if (textoEditado.trim().length < 15) {
            showErrorToast("El problema debe tener al menos 15 caracteres.");
            return;
        }

        try {
            const response = await fetch("/api/cliente/EditarComentarioSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vProblema: textoEditado.trim(),
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    vProblema: comentarioActualizado.vProblema,
                                    vFecha: comentarioActualizado.vFechaActualizacion || c.vFecha,
                                    vHora: comentarioActualizado.vHoraActualizacion || c.vHora,
                                    vUpdatedUser: comentarioActualizado.vUpdatedUser,
                                }
                                : c
                        )
                    );
                    cancelarEdicion();
                    showSuccessToast("Comentario editado exitosamente");
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast(response.status + " " + response.statusText);
            }
        } catch (error) {
            showErrorToast("Error al editar el comentario");
        }
    };

    const obtenerNivelesSoporte = async () => {
        setCargandoNiveles(true);
        try {
            const response = await fetch("/api/soportetecnico/ObtenerComboBoxesRegistroSoporte", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data && data.table1) {
                    setNivelesSoporte(data.table1);
                }
            }
        } catch (error) {
            console.error("Error al obtener niveles de soporte:", error);
        } finally {
            setCargandoNiveles(false);
        }
    };

    const cargarImagenPerfil = async (idUsuario) => {
        if (!idUsuario) return;
        setCargandoImagenes(prev => ({ ...prev, [idUsuario]: true }));

        try {
            const response = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${idUsuario}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'text/plain'
                }
            });

            if (response.ok) {
                const data = await response.text();
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: data }));
            } else {
                setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
            }
        } catch (error) {
            setImagenesPerfil(prev => ({ ...prev, [idUsuario]: '' }));
        } finally {
            setCargandoImagenes(prev => ({ ...prev, [idUsuario]: false }));
        }
    };

    const iniciarResolucion = async (comentario) => {
        setResolviendoId(comentario.idClientesAkasiaComentariosProblema);
        setTextoSolucion("");
        setNivelSoporteSeleccionado("");
        setMinutosInvertidos("");
    };

    const cancelarResolucion = () => {
        setResolviendoId(null);
        setTextoSolucion("");
        setNivelSoporteSeleccionado("");
        setMinutosInvertidos("");
    };

    const guardarResolucion = async (comentario) => {
        if (textoSolucion.trim().length < 15) {
            showErrorToast("La solución debe tener al menos 15 caracteres.");
            return;
        }
        if (!minutosInvertidos || parseInt(minutosInvertidos) <= 0) {
            showErrorToast("Debe ingresar los minutos invertidos (mayor a 0).");
            minutosRef.current?.focus();
            return;
        }
        if (!nivelSoporteSeleccionado) {
            showErrorToast("Debe seleccionar un nivel de soporte.");
            nivelRef.current?.focus();
            return;
        }

        try {
            const response = await fetch("/api/cliente/ResolverComentarioSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vSolucion: textoSolucion.trim(),
                    dTiempo: parseInt(minutosInvertidos),
                    idCatNivelSoporte: nivelSoporteSeleccionado,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];
                    const nivelObj = nivelesSoporte.find(n => n.idCatNivelSoporte.toString() === nivelSoporteSeleccionado.toString());

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    ...comentarioActualizado,
                                    dTiempo: parseInt(minutosInvertidos),
                                    vTiempoTexto: null,
                                    idCatNivelSoporte: nivelSoporteSeleccionado,
                                    vNombreNivel: nivelObj ? nivelObj.vNombreNivel : (comentarioActualizado?.vNombreNivel || c.vNombreNivel),
                                    iNivel: nivelObj ? nivelObj.iNivel : (comentarioActualizado?.iNivel || c.iNivel),
                                    bResuelto: true,
                                    vEstado: comentarioActualizado?.vEstado || "Resuelto",
                                    dFechaSolucion: comentarioActualizado?.dFechaSolucion || new Date().toISOString()
                                }
                                : c
                        )
                    );
                    cancelarResolucion();
                    showSuccessToast("Problema resuelto exitosamente");
                } else {
                    showErrorToast(data.vMessage);
                }
            }
        } catch (error) {
            showErrorToast("Error al resolver el comentario");
        }
    };

    const iniciarEdicionSolucion = async (comentario) => {
        setEditandoSolucionId(comentario.idClientesAkasiaComentariosProblema);
        const textoSinHTML = (comentario.vSolucion || "").replace(/<[^>]*>/g, "");
        setTextoSolucionEditado(textoSinHTML);
        setNivelSoporteEditado(comentario.idCatNivelSoporte || "");
        setMinutosInvertidosEditado(comentario.dTiempo || "");
    };

    const cancelarEdicionSolucion = () => {
        setEditandoSolucionId(null);
        setTextoSolucionEditado("");
        setNivelSoporteEditado("");
        setMinutosInvertidosEditado("");
    };

    const guardarEdicionSolucion = async (comentario) => {
        if (textoSolucionEditado.trim().length < 15) {
            showErrorToast("La solución debe tener al menos 15 caracteres.");
            return;
        }
        if (!minutosInvertidosEditado || parseInt(minutosInvertidosEditado) <= 0) {
            showErrorToast("Debe ingresar los minutos invertidos (mayor a 0).");
            return;
        }
        if (!nivelSoporteEditado) {
            showErrorToast("Debe seleccionar un nivel de soporte.");
            return;
        }

        try {
            const response = await fetch("/api/cliente/EditarSolucionProblemaSoporte", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClientesAkasiaComentariosProblema: comentario.idClientesAkasiaComentariosProblema,
                    vSolucion: textoSolucionEditado.trim(),
                    dTiempo: parseInt(minutosInvertidosEditado),
                    idCatNivelSoporte: nivelSoporteEditado,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const comentarioActualizado = JSON.parse(data.detalles)[0];
                    const nivelObj = nivelesSoporte.find(n => n.idCatNivelSoporte.toString() === nivelSoporteEditado.toString());

                    setComentarios((prev) =>
                        prev.map((c) =>
                            c.idClientesAkasiaComentariosProblema === comentario.idClientesAkasiaComentariosProblema
                                ? {
                                    ...c,
                                    ...comentarioActualizado,
                                    dTiempo: parseInt(minutosInvertidosEditado),
                                    vTiempoTexto: null,
                                    idCatNivelSoporte: nivelSoporteEditado,
                                    vNombreNivel: nivelObj ? nivelObj.vNombreNivel : (comentarioActualizado?.vNombreNivel || c.vNombreNivel),
                                    iNivel: nivelObj ? nivelObj.iNivel : (comentarioActualizado?.iNivel || c.iNivel),
                                    bResuelto: true,
                                    vEstado: comentarioActualizado?.vEstado || "Resuelto",
                                    dFechaSolucion: comentarioActualizado?.dFechaSolucion || new Date().toISOString()
                                }
                                : c
                        )
                    );
                    cancelarEdicionSolucion();
                    showSuccessToast("Solución actualizada exitosamente");
                }
            }
        } catch (error) {
            showErrorToast("Error al editar la solución");
        }
    };

    if (comentarios.length === 0) {
        return (
            <div className="text-center py-5">
                <span className="text-muted mb-3" style={{ fontSize: 40 }}>🗨️</span>
                <h6 className="text-muted mb-2">Sin comentarios</h6>
                <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                    No hay comentarios registrados para mostrar
                </p>
            </div>
        );
    }

    return (
        <Row className="g-2 card m-0 border-0 shadow-none">
            {comentarios.map((comentario, index) => (
                <ComentarioItem
                    key={`${comentario.idClientesAkasiaComentariosProblema}-${index}`}
                    comentario={comentario}
                    editandoId={editandoId}
                    resolviendoId={resolviendoId}
                    editandoSolucionId={editandoSolucionId}
                    textoEditado={textoEditado}
                    setTextoEditado={setTextoEditado}
                    textoSolucion={textoSolucion}
                    setTextoSolucion={setTextoSolucion}
                    textoSolucionEditado={textoSolucionEditado}
                    setTextoSolucionEditado={setTextoSolucionEditado}
                    nivelSoporteSeleccionado={nivelSoporteSeleccionado}
                    setNivelSoporteSeleccionado={setNivelSoporteSeleccionado}
                    minutosInvertidos={minutosInvertidos}
                    setMinutosInvertidos={setMinutosInvertidos}
                    nivelSoporteEditado={nivelSoporteEditado}
                    setNivelSoporteEditado={setNivelSoporteEditado}
                    minutosInvertidosEditado={minutosInvertidosEditado}
                    setMinutosInvertidosEditado={setMinutosInvertidosEditado}
                    nivelesSoporte={nivelesSoporte}
                    cargandoNiveles={cargandoNiveles}
                    cargandoImagenes={cargandoImagenes}
                    solucionTextareaRef={solucionTextareaRef}
                    minutosRef={minutosRef}
                    nivelRef={nivelRef}
                    iniciarEdicion={iniciarEdicion}
                    cancelarEdicion={cancelarEdicion}
                    guardarEdicion={guardarEdicion}
                    iniciarResolucion={iniciarResolucion}
                    cancelarResolucion={cancelarResolucion}
                    guardarResolucion={guardarResolucion}
                    iniciarEdicionSolucion={iniciarEdicionSolucion}
                    cancelarEdicionSolucion={cancelarEdicionSolucion}
                    guardarEdicionSolucion={guardarEdicionSolucion}
                    emailCliente={emailCliente}
                    setComentarios={setComentarios}
                />
            ))}
        </Row>
    );
};

ComentariosSoporte.propTypes = {
    idClienteAkasia: PropTypes.string,
    comentarios: PropTypes.array,
    setComentarios: PropTypes.func,
    emailCliente: PropTypes.string
};

export default ComentariosSoporte;